import { useQuery } from "@tanstack/react-query";
import React from "react";
import {
  GlobalSearchRequest,
  GlobalSearchResult,
  ISchedule
} from "../../../../api";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

export interface UseGlobalSearchOptions {
  enabled?: boolean;
}

export interface ProcessedGlobalSearchResult extends GlobalSearchResult {
  schedulesByUser: Record<string, ISchedule[]>;
  schedulesByResource: Record<string, ISchedule[]>;
  hasNoResults: boolean;
}

export function useGlobalSearch(
  params: GlobalSearchRequest,
  options?: UseGlobalSearchOptions
) {
  const { api } = useTacticHooks();

  // Only enable if explicitly enabled via options
  // `query` is now optional and can be an empty string
  const isEnabled = options?.enabled !== false;

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: [
      "globalSearch",
      params.query,
      params.office_ids,
      params.start_time,
      params.end_time
    ],

    queryFn: async () => {
      const res = await api.client.globalSearch.search(params);
      return res.result;
    },

    enabled: isEnabled,

    // 1 minute
    staleTime: 1000 * 60
  });

  // Process schedules to create {id: schedule[]} structure for users and resources
  const processedData = React.useMemo<
    ProcessedGlobalSearchResult | undefined
  >(() => {
    if (!data) return undefined;

    const schedulesByUser: Record<string, ISchedule[]> = {};
    const schedulesByResource: Record<string, ISchedule[]> = {};

    // Loop over schedules and extract users and resources
    if (data.schedules) {
      for (const schedule of data.schedules) {
        // Add main user
        if (schedule.user?.id) {
          if (schedulesByUser[schedule.user.id]) {
            schedulesByUser[schedule.user.id].push(schedule);
          } else {
            schedulesByUser[schedule.user.id] = [schedule];
          }
        }

        // Add creator
        if (schedule.creator?.id) {
          if (schedulesByUser[schedule.creator.id]) {
            schedulesByUser[schedule.creator.id].push(schedule);
          } else {
            schedulesByUser[schedule.creator.id] = [schedule];
          }
        }

        // Add organizer
        if (schedule.organizer?.id) {
          if (schedulesByUser[schedule.organizer.id]) {
            schedulesByUser[schedule.organizer.id].push(schedule);
          } else {
            schedulesByUser[schedule.organizer.id] = [schedule];
          }
        }

        // Resources this schedule occupies. Desk reservations from the
        // core API carry the resource on the SLOT rather than the
        // schedule root — merging only schedule.resource silently dropped
        // them, leaving search rows without their occupancy info.
        const resourceIds = new Set<string>();
        if (schedule.resource?.id) {
          resourceIds.add(schedule.resource.id);
        }

        // Add users and resources from slots
        if (schedule.slots) {
          for (const slot of schedule.slots) {
            if (slot.user?.id) {
              if (schedulesByUser[slot.user.id]) {
                schedulesByUser[slot.user.id].push(schedule);
              } else {
                schedulesByUser[slot.user.id] = [schedule];
              }
            }
            if (slot.resource?.id) {
              resourceIds.add(slot.resource.id);
            }
          }
        }

        for (const resourceId of resourceIds) {
          if (schedulesByResource[resourceId]) {
            schedulesByResource[resourceId].push(schedule);
          } else {
            schedulesByResource[resourceId] = [schedule];
          }
        }
      }
    }

    const hasNoResults =
      (!data.resources || data.resources.length === 0) &&
      (!data.users || data.users.length === 0) &&
      (!data.schedules || data.schedules.length === 0);

    return {
      ...data,
      schedulesByUser,
      schedulesByResource,
      hasNoResults
    };
  }, [data]);

  return {
    data: processedData,
    error,
    loading: isLoading,
    isLoading,
    refetch
  };
}
